import { Box, CircularProgress, Modal, Skeleton } from '@mui/material';
import { X } from 'feather-icons-react/build/IconComponents';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { CameraVideo, FiletypeCsv, Upload } from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';
import NEWAPI from '../API/newIndex';
import { NOPHOTO_IMAGE } from '../Assets/StaticData/StaticImage';
import SearchComponent from '../Component/FormInput/SearchComponent';
import SelectComponent from '../Component/FormInput/SelectComponent';
import Notification from '../Component/Notification';
import { upload } from '../redux/thunk/uploadThunk';
import OpenIframeModal from './OpenIframeModal';
import { removeAttachedImages } from './removeS3File';


const ImageUploader = ({
  alreadyImage,
  handleSetImage,
  folder, // current upload folder (for upload API)
  format, // 'image' | 'video' | 'csv'
  multiple,
  removeImages,
  setIsAddVideoLink,
  className,
  icon,
}) => {
  const [image, setimage] = useState();
  const [loader, setLoader] = useState(false);
  const [addNewFile, setAddNewFile] = useState(0);
  const [galleryLoader, setGalleryLoader] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(folder || 'all'); // tab: 'all' or folder name
  const [searchTerm, setSearchTerm] = useState(''); // 🔍 search text
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [imgErr, setImgErr] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState('');
  const [severity, setSeverity] = useState('error');
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [openIframeModal, setOpenIframeModal] = useState(false);
  const [iframeImageUrl, setIframeImageUrl] = useState('');
  const [galleryCache, setGalleryCache] = useState({});
  const dispatch = useDispatch();

  const openAiEditor = (imgUrl) => {
    setIframeImageUrl(imgUrl);
    setOpenIframeModal(true);
    setOpenModal(false);
  };

  // 🔹 Detect file type by extension
  const getFileType = (url) => {
    if (!url) return 'other';

    const ext = url.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'webp', 'svg', 'gif'].includes(ext)) return 'image';
    if (['mp4', 'avi', 'mov', 'mkv', 'webm', 'mpeg'].includes(ext)) return 'video';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'doc';
    if (['xls', 'xlsx'].includes(ext)) return 'xls';

    return 'other';
  };

  useEffect(() => {
    // if (alreadyImage) {
    setimage(alreadyImage);
    // }
  }, [alreadyImage]);

  /* ------------------ Notification Close ------------------ */
  const handleCloseNotification = () => {
    setIsNotification(false);
    setNotificationMsg('');
    setSeverity('error');
  };

  /* ------------------ Fetch Gallery Data ------------------ */
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 800);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  // FETCH DATA WHEN MODAL OPENS OR SEARCH CHANGES
  useEffect(() => {
    if (openModal) {
      const cacheKey = `${activeFolder}_${debouncedSearch}_${format ? format : 'all'}`;

      // If already cached → load from cache instantly
      if (galleryCache[cacheKey]) {
        const cached = galleryCache[cacheKey];
        setGalleryImages(cached.images);
        setFolders(cached.folders);
        setTotalPages(cached.totalPages);
        setHasMore(cached.hasMore);
      } else {
        // No cache → make first call
        fetchExistingFiles(1, activeFolder, debouncedSearch);
      }
    }
  }, [openModal, activeFolder, debouncedSearch, format]);

  const clearCacheForCurrentFolder = () => {
    const cacheKey = `${activeFolder}_${debouncedSearch}_${format ? format : 'all'}`;
    setGalleryCache((prev) => {
      const updated = { ...prev };
      delete updated[cacheKey];
      return updated;
    });
  };

  const validateCsvFile = (file) => {
    const ext = file.name?.split('.').pop()?.toLowerCase();
    if (ext !== 'csv') {
      setIsNotification(true);
      setSeverity('error');
      setNotificationMsg('File must be a CSV file.');
      return false;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setIsNotification(true);
      setSeverity('error');
      setNotificationMsg('CSV file size exceeds 10MB.');
      return false;
    }

    return true;
  };

  const prependToGalleryAndCache = (fileObj) => {
    const cacheKey = `${activeFolder}_${debouncedSearch}_${format ? format : 'all'}`;

    // 🔹 Update visible gallery
    setGalleryImages((prev) => [...fileObj, ...prev]);

    // 🔹 Update cache (if exists)
    setGalleryCache((prev) => {
      if (!prev[cacheKey]) return prev;

      return {
        ...prev,
        [cacheKey]: {
          ...prev[cacheKey],
          images: [...fileObj, ...prev[cacheKey].images],
        },
      };
    });
  };

  const fetchExistingFiles = async (pageToLoad = 1, folderFilter = activeFolder, search = '') => {
    const cacheKey = `${folderFilter}_${search}_${format ? format : 'all'}`;

    // ------------------------------
    // 1️⃣ If cached and page=1 → RETURN FROM CACHE (NO API CALL)
    // ------------------------------
    if (pageToLoad === 1 && galleryCache[cacheKey]) {
      const cached = galleryCache[cacheKey];

      setGalleryImages(cached.images);
      setFolders(cached.folders);
      setTotalPages(cached.totalPages);
      setHasMore(cached.hasMore);
      return; // ⛔ STOP → No API call
    }

    // ------------------------------
    // 2️⃣ Otherwise → Fetch from API
    // ------------------------------
    try {
      setGalleryLoader(true);
      const limit = 30;

      const filterParam = folderFilter === 'all' ? '' : folderFilter;

      const ApiCall = await NEWAPI({
        url: '/admin/fileupload/getAllImagesFromBucket',
        method: 'get',
        params: {
          folder: filterParam,
          page: pageToLoad,
          limit,
          // type: format,
          search,
        },
      });

      const result = ApiCall?.data;
      const newImages = result?.imageUrls || [];
      const totalPagesFromApi = result?.totalPages || 1;

      // Folder update
      if (Array.isArray(result?.folders)) {
        setFolders(result.folders);
      }

      const updatedImages = pageToLoad === 1 ? newImages : [...galleryImages, ...newImages];

      setGalleryImages(updatedImages);
      setTotalPages(totalPagesFromApi);
      setHasMore(pageToLoad < totalPagesFromApi && newImages.length > 0);

      // ------------------------------
      // 3️⃣ Save into CACHE for future usage (only page 1)
      // ------------------------------
      if (pageToLoad === 1) {
        setGalleryCache((prev) => ({
          ...prev,
          [cacheKey]: {
            images: newImages,
            folders: result?.folders || [],
            totalPages: totalPagesFromApi,
            hasMore: totalPagesFromApi > 1,
          },
        }));
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg('Failed to load files');
      setSeverity('error');
    } finally {
      setGalleryLoader(false);
    }
  };

  const handleSelectFromGallery = (fileUrl) => {
    // 🔥 DO NOT APPLY DIRECT
    setSelectedGalleryImage(fileUrl);
  };

  /* ---------------- APPLY SELECTED IMAGE (NEW BUTTON) ---------------- */
  const applySelectedImage = () => {
    if (!selectedGalleryImage) return;
    setimage(selectedGalleryImage);
    handleSetImage(selectedGalleryImage, multiple);
    setOpenModal(false);
  };

  const handleFiles = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const files = Array.from(selectedFiles);
    const uploadList = [];

    setLoader(true);
    setAddNewFile(files.length);

    try {
      for (const file of files) {
        const ext = file.name?.split('.').pop()?.toLowerCase();

        const imageExt = ['jpg', 'jpeg', 'png', 'webp', 'svg'];
        const videoExt = ['mp4', 'avi', 'mov', 'mkv', 'webm', 'mpeg', 'flv', 'wmv'];

        const isImage = imageExt.includes(ext);
        const isVideo = videoExt.includes(ext);
        const isCsv = ext === 'csv';

        if (!isImage && !isVideo && !isCsv) {
          setIsNotification(true);
          setSeverity('error');
          setNotificationMsg(`Invalid file format: ${file.name}`);
          continue;
        }

        if (isCsv) {
          if (!validateCsvFile(file)) continue;
          uploadList.push(file);
          continue;
        }

        // -------- IMAGE VALIDATION ---------
        if (isImage) {
          const maxImgSize = 10 * 1024 * 1024;
          if (file.size > maxImgSize) {
            setIsNotification(true);
            setSeverity('error');
            setNotificationMsg(`Image exceeds 10MB: ${file.name}`);
            continue;
          }

          // OG folder dimension validation
          if (folder === 'og') {
            const previewURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = previewURL;

            await new Promise((resolve) => (img.onload = resolve));

            if (img.width !== 1200 || img.height !== 630) {
              setIsNotification(true);
              setSeverity('error');
              setNotificationMsg('Image must be 1200 × 630px for OG.');
              continue;
            }
          }
        }

        // -------- VIDEO VALIDATION ---------
        if (isVideo) {
          const maxVideoSize = 100 * 1024 * 1024;
          if (file.size > maxVideoSize) {
            setIsNotification(true);
            setSeverity('error');
            setNotificationMsg(`Video exceeds 100MB: ${file.name}`);
            continue;
          }
        }

        // Add to upload list
        uploadList.push(file);
      }

      if (uploadList.length === 0) return;

      // -------- UPLOAD ---------
      const formData = new FormData();
      uploadList.forEach((file) => formData.append('file', file));
      formData.append('folder', folder);

      const res = await dispatch(upload(formData)).unwrap();
      const uploadedUrls = res?.data?.data || [];

      const finalFiles = uploadedUrls.map((url, i) => ({
        url,
        key: url.replace(`${process.env.REACT_APP_R2_CUSTOM_DOMAIN}/`, ''),
        lastModified: new Date().toISOString(),
        size: uploadList[i]?.size || 0,
      }));

      prependToGalleryAndCache(finalFiles);
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg('Upload failed!');
      setSeverity('error');
    } finally {
      setLoader(false);
      setAddNewFile(0);
    }
  };

  /* ------------------ REMOVE IMAGE / VIDEO ------------------ */
  const handleRemove = (e) => {
    e.stopPropagation();
    removeAttachedImages(image);
    handleSetImage('', multiple);
    removeImages('');
    setimage('');
  };

  /* ------------------ SCROLL HANDLER FOR INFINITE LOAD ------------------ */
  const handleGalleryScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && !galleryLoader && hasMore && page < totalPages) {
      fetchExistingFiles(page + 1, activeFolder, searchTerm);
    }
  };

  /* ------------------ FOLDER TAB CLICK ------------------ */
  const handleFolderClick = (folderName) => {
    if (folderName === activeFolder) return;
    setActiveFolder(folderName); // effect will refetch + includes searchTerm
  };

  const renderUploadIcons = (format) => {
    switch (format) {
      case 'video':
        return (
          <>
            {/* Upload icon */}
            <div className='w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md'>
              <Upload className='h-6 w-6' />
            </div>

            {/* Video icon */}
            <div className='w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md'>
              <CameraVideo className='h-6 w-6' />
            </div>

            {/* Add media URL */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(false);
                setIsAddVideoLink(true);
              }}
              className='z-10 px-3 h-10 flex items-center justify-center border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50'
            >
              {t('lang_Add_media_URL')}
            </button>
          </>
        );

      case 'csv':
        return (
          <>
            <div className='w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md font-semibold text-xs'>
              <FiletypeCsv className='h-6 w-6' />
            </div>
          </>
        );

      case 'image':
        return (
          <>
            <div className='w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md'>
              <Upload className='h-6 w-6' />
            </div>
          </>
        );

      default:
        return (
          <>
            {/* Upload */}
            <div className='w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md'>
              <Upload className='h-6 w-6' />
            </div>

            {/* Video */}
            <div className='w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md'>
              <CameraVideo className='h-6 w-6' />
            </div>

            {/* Media URL */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(false);
                setIsAddVideoLink(true);
              }}
              className='z-10 px-3 h-10 flex items-center justify-center border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50'
            >
              {t('Add media URL')}
            </button>
          </>
        );
    }
  };

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification
          message={notificationMsg}
          close={handleCloseNotification}
          severity={severity}
        />
      )}

      {/* ================= MAIN UPLOADER ================= */}
      <div
        onClick={() => !loader && setOpenModal(true)}
        className={
          className
            ? className
            : `border-2 w-full border-dashed border-gray-300 rounded-xl bg-white h-[180px]
         flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition gap-2`
        }
      >
        {icon ? (
          icon
        ) : (
          <>
            <div className='flex items-center gap-4'>{renderUploadIcons(format)}</div>

            <p className='text-sm text-gray-600 text-center'>
              {format === 'image'
                ? t('lang_Click_To_Upload_Image')
                : format === 'csv'
                  ? t('lang_Upload_CSV')
                  : t('lang_Upload_files')}
            </p>
          </>
        )}
      </div>

      {/* ================= SINGLE PREVIEW (ONLY IF NOT product-featured) ================= */}
      {image && multiple !== 'product-featured' && (
        <div className='flex relative items-center h-[200px] w-[200px] justify-center border main-border-color rounded-[6px] mt-3'>
          {format === 'image' ? (
            <img
              src={image}
              className='max-w-[180px] max-h-[180px]'
              onError={(e) => (e.currentTarget.src = NOPHOTO_IMAGE)}
            />
          ) : (
            <video src={image} controls width={180} />
          )}

          <button
            onClick={handleRemove}
            className='absolute top-0 right-0 w-[25px] h-[25px] flex items-center justify-center text-red-600 bg-white border'
          >
            <X className='w-4 h-4' />
          </button>
        </div>
      )}

      {/* ================= MODAL ================= */}
      <Modal
        open={openModal}
        onClose={() => !loader && setOpenModal(false)}
        className='flex items-center justify-center'
      >
        <Box className='bg-white p-4 rounded w-[720px] mx-auto h-[72vh] overflow-auto secondary-bg-color'>
          {/* header */}
          <div className='flex items-center justify-between'>
            <h6 className='text-md font-semibold'>{t('lang_Select_file')}</h6>
            <button
              onClick={() => setOpenModal(false)}
              className='flex items-center justify-center w-[25px] h-[25px] text-gray-500 border main-border-color rounded transition hover:bg-gray-200 hover:text-gray-700'
            >
              <X className='w-4 h-4' />
            </button>
          </div>

          {/* ===== Upload New Image / Video ===== */}
          <label className='file-tag-hover mt-2 flex items-center h-[150px] min-w-full rounded px-4 cursor-pointer flex-col justify-center space-y-2'>
            {/* <input
              type='file'
              accept='image/*, video/*'
              multiple={['product', 'product-featured'].includes(multiple)}
              onChange={(e) => handleFiles(e.target.files)}
              className='hidden'
              disabled={loader}
              /> */}

            <input
              type='file'
              accept={format === 'csv' ? '.csv' : format === 'image' ? 'image/*' : format === 'video' ? 'video/*' : format === 'media' ? 'image/*, video/*' : 'application/*'}
              multiple={['product', 'product-featured'].includes(multiple)}
              onChange={(e) => handleFiles(e.target.files)}
              className='hidden'
              disabled={loader}
            />

            <div className='flex items-center gap-4 mb-3'>{renderUploadIcons(format)}</div>

            {format === 'csv' && image && (
              <div className='flex items-center gap-2 mt-2 text-sm'>
                <span className='font-medium'>{t('lang_CSV_Uploaded')}:</span>
                <span className='truncate max-w-[250px]'>{image}</span>
                <button onClick={handleRemove} className='text-red-600 underline'>
                  {t('lang_Remove')}
                </button>
              </div>
            )}
            <p className='text-sm text-center'>
              {format === 'image'
                ? 'Click To Upload Image'
                : format === 'csv'
                  ? 'Drag & drop csv to upload'
                  : 'Drag & drop images, videos, docs, 3D models to upload'}
            </p>
            <p className='text-xs text-gray-400 mt-1'>{t('lang_size_required')}</p>
          </label>

          {/* ===== Folder Tabs ===== */}
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4'>
            <div className='mt-2 w-full sm:w-[200px]'>
              <SelectComponent
                value={activeFolder}
                onChange={(e) => handleFolderClick(e.target.value)}
                options={[
                  { value: 'all', label: 'All' },
                  ...(!folders.includes(activeFolder) ? [{ value: activeFolder, label: activeFolder.replace('-', ' ') }] : []),
                  ...(folders || []).map((f) => ({ value: f, label: f.replace('-', ' ') })),
                ]}
                className='w-full'
                size='sm'
              />
            </div>

            {/* 🔍 SEARCH INPUT */}
            <div className='mt-2 w-full sm:w-[240px]'>
              <SearchComponent
                placeholder={t('lang_Search_files_by_name')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm('')}
                size='sm'
              />
            </div>
          </div>

          {/* ===== Gallery (images / videos / docs) ===== */}
          <div className='mt-4'>
            {galleryImages?.length === 0 && galleryLoader ? (
              <div className='grid grid-cols-5 gap-3 max-h-[300px] overflow-hidden'>
                {Array.from({ length: 20 }).map((_, i) => (
                  <div
                    key={i}
                    className='border rounded-md p-2 flex flex-col items-center justify-center'
                  >
                    <Skeleton variant='rectangular' width='100%' height={80} />
                  </div>
                ))}
              </div>
            ) : galleryImages?.length > 0 ? (
              <div
                className='grid grid-cols-5 gap-3 max-h-[300px] overflow-auto'
                onScroll={handleGalleryScroll}
              >
                {loader && addNewFile > 0 && (
                  <>
                    {Array.from({ length: addNewFile }).map((_, i) => (
                      <div
                        key={i}
                        className='border rounded-md p-2 flex flex-col items-center justify-center'
                      >
                        <Skeleton variant='rectangular' width='100%' height={80} />
                      </div>
                    ))}
                  </>
                )}
                {galleryImages?.map((fileUrl, i) => {
                  const fileType = getFileType(fileUrl?.url);

                  return (
                    <div
                      key={i}
                      onClick={() => handleSelectFromGallery(fileUrl?.url)}
                      className={`border main-border-color rounded-md p-2 cursor-pointer hover:border-black flex flex-col items-center justify-center text-center ${selectedGalleryImage === fileUrl?.url ? 'border-black' : ''
                        }`}
                    >
                      {/* IMAGE */}
                      {fileType === 'image' && (
                        <img
                          src={fileUrl?.url}
                          className='w-full h-[80px] object-contain'
                          alt={`gallery-${i}`}
                        />
                      )}

                      {/* VIDEO */}
                      {fileType === 'video' && (
                        <video
                          src={fileUrl?.url}
                          className='w-full h-[80px] object-contain'
                          muted
                        />
                      )}

                      {/* PDF */}
                      {fileType === 'pdf' && (
                        <div className='flex flex-col items-center justify-center h-[80px]'>
                          <span className='text-red-600 text-3xl'>📄</span>
                          <span className='text-[10px] mt-1'>{t('lang_PDF')}</span>
                        </div>
                      )}

                      {/* DOC */}
                      {fileType === 'doc' && (
                        <div className='flex flex-col items-center justify-center h-[80px]'>
                          <span className='text-blue-600 text-3xl'>📄</span>
                          <span className='text-[10px] mt-1'>{t('lang_DOC')}</span>
                        </div>
                      )}

                      {/* XLS */}
                      {fileType === 'xls' && (
                        <div className='flex flex-col items-center justify-center h-[80px]'>
                          <span className='text-green-600 text-3xl'>📊</span>
                          <span className='text-[10px] mt-1'>{t('lang_XLS')}</span>
                        </div>
                      )}

                      {/* OTHER */}
                      {fileType === 'other' && (
                        <div className='flex flex-col items-center justify-center h-[80px]'>
                          <span className='text-gray-600 text-3xl'>
                            <FiletypeCsv className='h-12 w-12' />
                          </span>
                          <span className='text-[10px] mt-1'>
                            {fileUrl?.key ? fileUrl.key.split('/').pop().slice(-35) : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Bottom loader for next page */}
                {galleryLoader && hasMore && (
                  <div className='col-span-5 flex justify-center py-2'>
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            ) : (
              <p className='text-center text-sm text-gray-400 mt-4'>{t('lang_No_files_found')}</p>
            )}
          </div>

          {selectedGalleryImage && (
            <div className='mt-4 flex justify-end gap-2'>
              <button
                onClick={applySelectedImage}
                className='px-4 py-2 bg-black text-white rounded-md'
              >
                {t('lang_Select')}
              </button>
              {format === 'image' && (
                <button
                  onClick={() => openAiEditor(selectedGalleryImage)}
                  className='px-4 py-2 bg-black text-white rounded-md'
                >
                  {t('lang_AI_Editor')}
                </button>
              )}
            </div>
          )}
        </Box>
      </Modal>

      <OpenIframeModal
        openIframeModal={openIframeModal}
        setOpenIframeModal={setOpenIframeModal}
        iframeImageUrl={iframeImageUrl}
        multiple={multiple}
        handleSetImage={handleSetImage}
        setIsNotification={setIsNotification}
        setNotificationMsg={setNotificationMsg}
        setSeverity={setSeverity}
        folder={folder}
        editUrl=''
        setLoader={setLoader}
      />

      {/* ================= ERROR NOTE ================= */}
      {imgErr && format === 'image' && (
        <span className='text-xs bg-yellow-100 mt-1 inline-block px-3 py-1 rounded-[6px]'>
          <span className='font-semibold'>{t('lang_Note')} :</span> {t('lang_File_should_be_in_jpg_jpeg_png_webp_or_svg_format_Max_Size_10_MB')}
        </span>
      )}
    </>
  );
};

export default ImageUploader;
